import { useMemo, useState, type ComponentType } from "react";
import { useQueries } from "@tanstack/react-query";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getDateRangeDates } from "@/lib/compare-date-utils";
import {
  useClicksTrend,
  useGscChartTrend,
  useClicksCompareTrends,
  type ClicksParams,
} from "@/hooks/useClicksData";
import { useKeywordVolumeTrend } from "@/hooks/useKeywordVolumes";
import { keywordVolumeService } from "@/services/KeywordVolumeService";
import { CURRENCY_SYMBOL } from "@/config/currency";
import type { Json } from "@/types/database";
import { DateRangeFilter, type DateRangeOption } from "./DateRangeFilter";
import { COMPARE_COLORS, compareFiltersFor, enrichPoint } from "./chartData";
import {
  ALL_METRICS,
  formatMetricValue,
  getDimensionLabel,
  metricLabel,
  type MetricFormat,
} from "./metricDisplay";
import type { ClicksTrendGranularity } from "@/services/ClicksService";

type SeriesAxis = "count" | "money" | "pct" | "pos" | "vol";
type SeriesFormat = MetricFormat | "position";

const GSC_GROUP = "Search (GSC)";
const KV_GROUP = "Keyword Volume"; // keyword volume

type SeriesOption = {
  id: string;
  color: string;
  axis: SeriesAxis;
  format: SeriesFormat;
  group: string;
};

const METRIC_LABELS = new Map<string, string>([
  ...ALL_METRICS.map((m): [string, string] => [m.id, m.label]),
  ["kv_volume", "Keyword Volume"], // keyword volume
]);
const labelFor = (id: string): string => METRIC_LABELS.get(id) ?? metricLabel(id);

// Metrics grouped for the chips; each carries the axis it plots on. Cost-based
// metrics are gone (cost is always 0). Keitaro "clicks" and GSC "clicks" are
// different funnels — separate groups, tooltip names each in full.
const SERIES: SeriesOption[] = [
  { id: "total_clicks", color: "#6366f1", axis: "count", format: "int", group: "Counts" },
  { id: "unique_global", color: "#8b5cf6", axis: "count", format: "int", group: "Counts" },
  { id: "total_conversions", color: "#f59e0b", axis: "count", format: "int", group: "Counts" },
  { id: "total_leads", color: "#3b82f6", axis: "count", format: "int", group: "Counts" },
  { id: "total_sales", color: "#ec4899", axis: "count", format: "int", group: "Counts" },
  { id: "total_bots", color: "#a1a1aa", axis: "count", format: "int", group: "Counts" },
  { id: "total_revenue", color: "#10b981", axis: "money", format: "currency", group: "Money" },
  { id: "epc", color: "#14b8a6", axis: "money", format: "rate", group: "Money" },
  { id: "uepc", color: "#06b6d4", axis: "money", format: "rate", group: "Money" },
  { id: "ecpm", color: "#f97316", axis: "money", format: "currency", group: "Money" },
  { id: "cr_percent", color: "#6366f1", axis: "pct", format: "percent", group: "Rates & %" },
  { id: "cr_leads_percent", color: "#3b82f6", axis: "pct", format: "percent", group: "Rates & %" },
  { id: "cr_sales_percent", color: "#ec4899", axis: "pct", format: "percent", group: "Rates & %" },
  { id: "bot_share_percent", color: "#a1a1aa", axis: "pct", format: "percent", group: "Rates & %" },
  { id: "gsc_clicks", color: "#0ea5e9", axis: "count", format: "int", group: GSC_GROUP },
  { id: "gsc_impressions", color: "#64748b", axis: "count", format: "int", group: GSC_GROUP },
  { id: "gsc_ctr", color: "#a855f7", axis: "pct", format: "percent", group: GSC_GROUP },
  { id: "gsc_position", color: "#eab308", axis: "pos", format: "position", group: GSC_GROUP },
  // keyword volume: monthly; a day/week bucket shows its month's value.
  { id: "kv_volume", color: "#84cc16", axis: "vol", format: "int", group: KV_GROUP },
];
const SERIES_BY_ID = new Map(SERIES.map((s) => [s.id, s]));
const GROUP_ORDER = ["Counts", "Money", "Rates & %", GSC_GROUP, KV_GROUP];
// Metrics you can compare across entities — GSC excluded (no per-entity GSC here).
// keyword volume is per entity too (its own trend per compared value).
const COMPARE_METRICS = SERIES.filter((s) => s.group !== GSC_GROUP);
// Compare: colour = entity, dash = metric, so N entities x M metrics stay readable.
const COMPARE_DASHES = [undefined, "6 3", "2 3", "10 4 2 4"];

const CHART_PRESETS: { value: DateRangeOption; label: string }[] = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_7_days", label: "Last 7 Days" },
  { value: "last_14_days", label: "Last 14 Days" },
  { value: "last_21_days", label: "Last 21 Days" },
  { value: "last_30_days", label: "Last 30 Days" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "all_time", label: "All Time" },
  { value: "custom", label: "Custom Range" },
];
const PRESET_LABEL = new Map(CHART_PRESETS.map((p) => [p.value, p.label]));

const fmtValue = (value: number, format: SeriesFormat): string =>
  format === "position" ? value.toFixed(1) : formatMetricValue(value, format);

const compactFmt = new Intl.NumberFormat("en-US", { notation: "compact" });

const axisTickFormatter = (
  axis: SeriesAxis,
): ((v: number) => string) | undefined => {
  if (axis === "money") return (v) => `${CURRENCY_SYMBOL}${v}`;
  if (axis === "pct") return (v) => `${v}%`;
  if (axis === "pos") return (v) => v.toFixed(0);
  if (axis === "vol") return (v) => compactFmt.format(v);
  return undefined;
};

type ChartPoint = Record<string, number | string>;

const formatBucket = (d: string, g: ClicksTrendGranularity): string => {
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return d;
  if (g === "month")
    return date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

// Bucket size follows the span, so there's no separate Day/Week/Month control.
const granularityForSpan = (
  dateRange: DateRangeOption,
  customFrom?: string,
  customTo?: string,
): ClicksTrendGranularity => {
  const { current } = getDateRangeDates(dateRange, new Date(), customFrom, customTo);
  if (!current.start) return "month";
  const days = Math.round(
    (new Date(current.end).getTime() - new Date(current.start).getTime()) /
      86_400_000,
  );
  if (days <= 45) return "day";
  if (days <= 186) return "week";
  return "month";
};

interface ClicksChartProps {
  params: ClicksParams;
  /** Drill-down: scope the trend to a table value (null = aggregate). */
  focus?: { key: string; value: string; label: string } | null;
  onClearFocus?: () => void;
  /** Compare overlay: dimension values checked in the table (≤ COMPARE_MAX). */
  compareValues?: string[];
  /** Each compared value's dimension_parts, when its row has them. */
  compareParts?: ReadonlyMap<string, string[] | null | undefined>;
  /** How many rows are selected in the table; compareValues holds the first few. */
  selectedCount?: number;
  onClearCompare?: () => void;
}

export function ClicksChart({
  params,
  focus,
  onClearFocus,
  compareValues = [],
  compareParts,
  selectedCount,
  onClearCompare,
}: ClicksChartProps) {
  const [chartType, setChartType] = useState<"line" | "area" | "bar">("line");
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(["total_clicks", "total_revenue"]),
  );
  const [compareMetrics, setCompareMetrics] = useState<string[]>(["total_revenue"]);
  const toggleCompareMetric = (id: string) =>
    setCompareMetrics((cur) =>
      cur.includes(id)
        ? cur.length > 1 ? cur.filter((m) => m !== id) : cur // keep at least one
        : cur.length >= COMPARE_DASHES.length ? cur : [...cur, id],
    );
  const [open, setOpen] = useState(true);
  const [linked, setLinked] = useState(false);
  const [chartRange, setChartRange] = useState<DateRangeOption>("last_30_days");
  const [chartFrom, setChartFrom] = useState<string | undefined>();
  const [chartTo, setChartTo] = useState<string | undefined>();

  const compareMode = compareValues.length > 0;
  const compareLabel = getDimensionLabel(params.groupBy);

  // The dimensions behind a checked row. A composite row ("GB ▸ Drip Casino"
  // under country ▸ brand) scopes the trend by each of its parts
  // (compareFiltersFor), so multi-dimension rows overlay on the chart too.
  const compareDims =
    params.groupDims && params.groupDims.length > 0
      ? params.groupDims
      : [params.groupBy];
  const makeCompareFilters = (value: string) =>
    compareFiltersFor(value, compareDims, compareParts?.get(value));

  const effective = linked
    ? {
        dateRange: params.dateRange,
        customFrom: params.customFrom,
        customTo: params.customTo,
      }
    : { dateRange: chartRange, customFrom: chartFrom, customTo: chartTo };

  const granularity = granularityForSpan(
    effective.dateRange,
    effective.customFrom,
    effective.customTo,
  );

  const activeFilters = focus
    ? {
        ...params.activeFilters,
        [focus.key]: { mode: "include" as const, values: [focus.value] },
      }
    : params.activeFilters;

  const { data: trend = [], isFetching } = useClicksTrend({
    activeFilters,
    granularity,
    dateRange: effective.dateRange,
    customFrom: effective.customFrom,
    customTo: effective.customTo,
  });

  const activeSeries = SERIES.filter((s) => selected.has(s.id));
  const gscSelected = activeSeries.some((s) => s.group === GSC_GROUP);
  const gscDomain = focus?.key === "domain" ? focus.value : undefined;

  const { data: gscTrend = [] } = useGscChartTrend(
    {
      granularity,
      dateRange: effective.dateRange,
      customFrom: effective.customFrom,
      customTo: effective.customTo,
      domain: gscDomain,
    },
    gscSelected && !compareMode,
  );

  // keyword volume
  const kvSelected = selected.has("kv_volume");
  const kvRange = getDateRangeDates(
    effective.dateRange,
    new Date(),
    effective.customFrom,
    effective.customTo,
  ).current;
  const { data: kvTrend = [], isFetched: kvFetched } = useKeywordVolumeTrend(
    kvRange.start,
    kvRange.end,
    activeFilters,
    kvSelected && !compareMode,
  );
  const kvByMonth = useMemo(
    () => new Map(kvTrend.map((k) => [k.month.slice(0, 7), k])),
    [kvTrend],
  );

  const gscByBucket = useMemo(
    () => new Map(gscTrend.map((g) => [g.bucket_date, g])),
    [gscTrend],
  );

  const data = useMemo<ChartPoint[]>(
    () =>
      trend.map((p) => {
        const g = gscByBucket.get(p.bucket_date);
        const kv = kvByMonth.get(p.bucket_date.slice(0, 7));
        return {
          bucket_date: p.bucket_date,
          ...enrichPoint(p),
          ...(kv ? { kv_volume: kv.volume, kv_keywords: kv.keywords } : {}),
          ...(g
            ? {
                gsc_clicks: g.gsc_clicks,
                gsc_impressions: g.gsc_impressions,
                gsc_ctr: g.gsc_ctr,
                gsc_position: g.gsc_position,
              }
            : {}),
        };
      }),
    [trend, gscByBucket, kvByMonth],
  );

  // Compare overlay: one trend per checked value, merged on the bucket date, the
  // chosen metric extracted per entity into its own column (e0, e1, …).
  const compareResults = useClicksCompareTrends(
    compareMode ? compareValues : [],
    makeCompareFilters,
    {
      activeFilters: params.activeFilters,
      granularity,
      dateRange: effective.dateRange,
      customFrom: effective.customFrom,
      customTo: effective.customTo,
    },
  );
  // keyword volume per compared value, only when it is one of the metrics.
  const compareKv = useQueries({
    queries: (compareMode && compareMetrics.includes("kv_volume") ? compareValues : []).map((value) => ({
      queryKey: ["keywordVolumeTrend", kvRange.start, kvRange.end, value, params.activeFilters, compareDims],
      queryFn: () =>
        keywordVolumeService
          .getTrend(kvRange.start, kvRange.end, {
            ...params.activeFilters,
            ...makeCompareFilters(value),
          } as Json)
          .catch(() => []),
      staleTime: 1000 * 60 * 30,
    })),
  });
  const compareFetching = compareResults.some((r) => r.isFetching);
  const compareData = useMemo<ChartPoint[]>(() => {
    if (!compareMode) return [];
    const byBucket = new Map<string, ChartPoint>();
    compareResults.forEach((res, i) => {
      (res.data ?? []).forEach((p) => {
        const row = byBucket.get(p.bucket_date) ?? { bucket_date: p.bucket_date };
        const point = enrichPoint(p);
        for (const metric of compareMetrics)
          if (metric !== "kv_volume") row[`${metric}__e${i}`] = point[metric] ?? 0;
        byBucket.set(p.bucket_date, row);
      });
    });
    // keyword volume goes on EVERY bucket by its month, not only on buckets
    // where that entity had clicks: an entity with clicks in one month would
    // otherwise get no volume line at all (30.09.2026, nevadakasyno.pl).
    compareKv.forEach((res, i) => {
      const byMonth = new Map((res.data ?? []).map((k) => [k.month.slice(0, 7), k.volume]));
      for (const row of byBucket.values()) {
        const v = byMonth.get(String(row.bucket_date).slice(0, 7));
        if (v != null) row[`kv_volume__e${i}`] = v;
      }
    });
    return [...byBucket.values()].sort((a, b) =>
      String(a.bucket_date).localeCompare(String(b.bucket_date)),
    );
    // compareResults is a fresh array each render; recomputing over ≤5×~30 pts
    // is cheap. Keyed on the values themselves, not their count: with more rows
    // selected than drawn, the drawn five change while the count stays 5, and
    // lines e0..e4 must follow their entities.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compareMode, compareMetrics, compareValues.join("\u0000"), JSON.stringify(
    [...compareResults, ...compareKv].map((r) => r.dataUpdatedAt),
  )]);

  const plotData = compareMode ? compareData : data;
  const plotFetching = compareMode ? compareFetching : isFetching;

  const settlingBuckets = granularity === "day" ? 3 : 1;
  const settlingStart =
    plotData.length > 0
      ? (plotData[Math.max(0, plotData.length - settlingBuckets)]
          .bucket_date as string)
      : undefined;
  const lastBucket =
    plotData.length > 0
      ? (plotData[plotData.length - 1].bucket_date as string)
      : undefined;
  const showDots = plotData.length > 0 && plotData.length <= 31;
  const singleBucket = plotData.length === 1;

  const toggle = (id: string) =>
    setSelected((cur) => {
      const next = new Set(cur);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  // Axes: normal mode uses each selected metric's axis; compare mode uses just
  // the compared metric's axis.
  const usedAxes = compareMode
    ? new Set<SeriesAxis>(compareMetrics.map((m) => SERIES_BY_ID.get(m)?.axis ?? "count"))
    : new Set(activeSeries.map((s) => s.axis));
  const referenceAxis = [...usedAxes][0] ?? "count";

  const hasSeries = compareMode ? true : activeSeries.length > 0;

  const ChartComp = (
    chartType === "area" ? AreaChart : chartType === "bar" ? BarChart : LineChart
  ) as unknown as ComponentType<Record<string, unknown>>;
  const SeriesComp = (
    chartType === "area" ? Area : chartType === "bar" ? Bar : Line
  ) as unknown as ComponentType<Record<string, unknown>>;

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="text-sm font-semibold text-foreground"
        >
          Trend {open ? "▾" : "▸"}
        </button>

        {focus && !compareMode ? (
          <button
            type="button"
            onClick={onClearFocus}
            title="Back to the aggregate trend"
            className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary hover:bg-primary/20"
          >
            {focus.label}
            <span aria-hidden>✕</span>
          </button>
        ) : null}

        {compareMode ? (
          <button
            type="button"
            onClick={onClearCompare}
            title="Clear the selection (unchecks the rows in the table too)"
            className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary hover:bg-primary/20"
          >
            Comparing {compareValues.length}
            {selectedCount && selectedCount > compareValues.length
              ? ` of ${selectedCount} selected`
              : ""}{" "}
            {compareLabel}
            <span aria-hidden>✕</span>
          </button>
        ) : null}

        {open ? (
          <div className="ml-auto flex items-center gap-1">
            <span className="text-xs text-muted-foreground">Chart:</span>
            <button
              type="button"
              onClick={() => setLinked(false)}
              title="Independent — the chart has its own Date Range below"
              className={cn(
                "cursor-pointer rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                !linked
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/70",
              )}
            >
              Independent
            </button>
            <button
              type="button"
              onClick={() => setLinked(true)}
              title="Linked — follows the table's selected Date Range"
              className={cn(
                "cursor-pointer rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
                linked
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/70",
              )}
            >
              Linked to table
            </button>
            <span className="mx-1 h-4 w-px bg-border" />
            <Select
              value={chartType}
              onValueChange={(v) => setChartType(v as typeof chartType)}
            >
              <SelectTrigger className="h-8 w-[120px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="line">Line</SelectItem>
                <SelectItem value="area">Area</SelectItem>
                <SelectItem value="bar">Bar</SelectItem>
              </SelectContent>
            </Select>
          </div>
        ) : null}
      </div>

      {open ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {linked ? (
              <span className="text-xs text-muted-foreground">
                Range: follows the table
                <span className="ml-1 font-medium text-foreground">
                  ({PRESET_LABEL.get(params.dateRange) ?? params.dateRange})
                </span>
              </span>
            ) : (
              <DateRangeFilter
                dateRange={chartRange}
                onDateRangeChange={setChartRange}
                compareEnabled={false}
                onCompareChange={() => {}}
                customFrom={chartFrom}
                customTo={chartTo}
                onCustomDateChange={(type, date) =>
                  type === "from" ? setChartFrom(date) : setChartTo(date)
                }
                options={CHART_PRESETS}
                showCompare={false}
              />
            )}
            <span className="rounded bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              {granularity === "day"
                ? "daily"
                : granularity === "week"
                  ? "weekly"
                  : "monthly"}{" "}
              buckets
            </span>
          </div>

          {compareMode ? (
            // Compare overlay: one metric, one line per checked entity. The
            // per-metric chips don't apply, so offer a single metric picker.
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-[11px] font-medium text-muted-foreground">
                Metrics to compare (up to {COMPARE_DASHES.length}):
              </span>
              {COMPARE_METRICS.map((m) => {
                const i = compareMetrics.indexOf(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => toggleCompareMetric(m.id)}
                    title={i >= 0 ? "Line style of this metric in the chart" : undefined}
                    className={cn(
                      "flex cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors",
                      i >= 0
                        ? "border-foreground/40 bg-foreground/5 text-foreground"
                        : "border-border bg-muted text-muted-foreground hover:bg-muted/70",
                    )}
                  >
                    {i >= 0 ? (
                      <svg width="18" height="6" aria-hidden>
                        <line x1="0" y1="3" x2="18" y2="3" stroke="currentColor" strokeWidth="2" strokeDasharray={COMPARE_DASHES[i]} />
                      </svg>
                    ) : null}
                    {labelFor(m.id)}
                  </button>
                );
              })}
              <span className="text-xs text-muted-foreground">
                Uncheck rows in the table to remove lines.
              </span>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              {GROUP_ORDER.map((group) => (
                <div key={group} className="flex flex-wrap items-center gap-1.5">
                  <span className="w-24 shrink-0 text-[11px] font-medium text-muted-foreground">
                    {group}
                  </span>
                  {SERIES.filter((s) => s.group === group).map((s) => {
                    const on = selected.has(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => toggle(s.id)}
                        className={cn(
                          "flex cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1 text-xs transition-colors",
                          on
                            ? "border-transparent text-white"
                            : "border-border bg-muted text-muted-foreground hover:bg-muted/70",
                        )}
                        style={on ? { backgroundColor: s.color } : undefined}
                      >
                        <span
                          className="size-2 rounded-full"
                          style={{ backgroundColor: on ? "#fff" : s.color }}
                        />
                        {labelFor(s.id)}
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          )}

          {singleBucket ? (
            <div className="rounded-md bg-amber-500/10 px-3 py-1.5 text-xs text-amber-700 dark:text-amber-400">
              This range is a single {granularity} — the point shows, but there's
              no line to draw. Pick a wider range.
            </div>
          ) : null}

          {/* keyword volume: monthly, and the newest export ends before today */}
          {kvSelected && !compareMode && kvFetched && kvTrend.length === 0 ? (
            <div className="rounded-md bg-amber-500/10 px-3 py-1.5 text-xs text-amber-700 dark:text-amber-400">
              Keyword Volume has no months in this range: it is monthly and the
              latest uploaded month is older than the range. Pick a longer one
              (e.g. All Time or a custom range over 2026).
            </div>
          ) : null}

          {plotData.length === 0 ? (
            <div className="flex h-[300px] items-center justify-center text-center text-sm text-muted-foreground">
              {plotFetching ? "Loading…" : "No data for this range."}
            </div>
          ) : !hasSeries ? (
            <div className="flex h-[300px] items-center justify-center text-sm text-muted-foreground">
              Pick at least one metric above.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <ChartComp
                data={plotData}
                margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#8884" />
                {settlingStart && lastBucket ? (
                  <ReferenceArea
                    yAxisId={referenceAxis}
                    x1={settlingStart}
                    x2={lastBucket}
                    fill="#888"
                    fillOpacity={0.12}
                    label={{
                      value: "settling",
                      position: "insideTop",
                      fontSize: 10,
                      fill: "#999",
                    }}
                  />
                ) : null}
                <XAxis
                  dataKey="bucket_date"
                  tickFormatter={(d: string) => formatBucket(d, granularity)}
                  tick={{ fontSize: 11, fill: "#888" }}
                  minTickGap={24}
                />
                {[...usedAxes].map((axis) => (
                  <YAxis
                    key={axis}
                    yAxisId={axis}
                    orientation={axis === "count" ? "left" : "right"}
                    reversed={axis === "pos"}
                    width={axis === "money" ? 56 : axis === "pos" ? 40 : 48}
                    tick={{ fontSize: 11, fill: "#888" }}
                    tickFormatter={axisTickFormatter(axis)}
                  />
                ))}
                <Tooltip
                  labelFormatter={(d) => formatBucket(String(d), granularity)}
                  formatter={(value, name, item) => {
                    if (compareMode) {
                      const metric = String((item as { dataKey?: string })?.dataKey ?? "").split("__e")[0];
                      return [fmtValue(Number(value), SERIES_BY_ID.get(metric)?.format ?? "int"), String(name)];
                    }
                    const id = String(
                      (item as { dataKey?: string })?.dataKey ?? "",
                    );
                    const s = SERIES_BY_ID.get(id);
                    // keyword volume: say how many keywords the line stands on
                    const kw = (item as { payload?: ChartPoint })?.payload?.kv_keywords;
                    return [
                      fmtValue(Number(value), s ? s.format : "int"),
                      id === "kv_volume" && kw != null
                        ? `${labelFor(id)} (${kw} keywords)`
                        : s ? labelFor(id) : String(name),
                    ];
                  }}
                  contentStyle={{ fontSize: 12 }}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {compareMode
                  ? compareValues.flatMap((value, i) =>
                      compareMetrics.map((metric, mi) => {
                        const kv = metric === "kv_volume";
                        return (
                          <SeriesComp
                            key={`${metric}__${value}`}
                            yAxisId={SERIES_BY_ID.get(metric)?.axis ?? "count"}
                            // keyword volume is monthly: a step per month, not
                            // a curve through made-up days in between.
                            type={kv ? "stepAfter" : "monotone"}
                            dataKey={`${metric}__e${i}`}
                            name={compareMetrics.length > 1 ? `${value} · ${labelFor(metric)}` : value}
                            stroke={COMPARE_COLORS[i % COMPARE_COLORS.length]}
                            strokeDasharray={COMPARE_DASHES[mi]}
                            fill={COMPARE_COLORS[i % COMPARE_COLORS.length]}
                            fillOpacity={chartType === "area" ? 0.2 : 1}
                            dot={showDots && !kv}
                            connectNulls={!kv}
                          />
                        );
                      }),
                    )
                  : activeSeries.map((s) => (
                      <SeriesComp
                        key={s.id}
                        yAxisId={s.axis}
                        // keyword volume is monthly: steps, dashed (SEO advice)
                        type={s.id === "kv_volume" ? "stepAfter" : "monotone"}
                        strokeDasharray={s.id === "kv_volume" ? "6 3" : undefined}
                        dataKey={s.id}
                        name={labelFor(s.id)}
                        stroke={s.color}
                        fill={s.color}
                        fillOpacity={chartType === "area" ? 0.25 : 1}
                        dot={showDots}
                        connectNulls
                      />
                    ))}
              </ChartComp>
            </ResponsiveContainer>
          )}
        </>
      ) : null}
    </div>
  );
}
